import type { Constructor, LitMixinBase } from './mixin-types';
import { property } from 'lit/decorators.js';
import { ContextProvider } from '@lit/context';
import { RealtimeContext } from '../contexts/realtime-context';
import { RealtimeConnection } from '../realtime/realtime-connection';

// Provider mixin — owned by app-index, paired with WithUserProfile and
// WithActiveShop. Constructs the RealtimeConnection lazily on first use
// (which is when app-index calls connectRealtime() after a user profile
// is set), tears it down on logout, and re-issues joinShop/leaveShop as
// the active shop changes.
export const WithRealtime = <T extends LitMixinBase>(superClass: T) => {
  class WithRealtimeClass extends superClass {
    private _realtimeProvider = new ContextProvider(this, {
      context: RealtimeContext,
    });

    @property({ attribute: false })
    private _realtime?: RealtimeConnection;

    public get realtime(): RealtimeConnection | undefined {
      return this._realtime;
    }

    // Called from app-index after a user-profile-set event. The connection
    // is per-user (negotiate stamps the userId), so a fresh user gets a
    // fresh connection.
    public async connectRealtime(): Promise<void> {
      if (!this._realtime) {
        this._realtime = new RealtimeConnection();
        // Phase Q.1 — wire the shared connection-state store to this
        // RealtimeConnection so the app-shell badge tracks reconnects
        // for the duration of this user session.
        const { connectionStateStore } =
          await import('../state/connection-state');
        connectionStateStore.attachRealtime(this._realtime);
        this._realtimeProvider.setValue(this._realtime);
        this.requestUpdate();
      }
      await this._realtime.connect();
    }

    public async disconnectRealtime(): Promise<void> {
      if (!this._realtime) return;
      await this._realtime.disconnect();
      this._realtime = undefined;
      this._realtimeProvider.setValue(undefined);
      this.requestUpdate();
    }

    public async realtimeJoinShop(shopName: string): Promise<void> {
      if (!this._realtime) await this.connectRealtime();
      await this._realtime?.joinShop(shopName);
    }

    public async realtimeLeaveShop(): Promise<void> {
      await this._realtime?.leaveCurrentShop();
    }
  }

  return WithRealtimeClass as Constructor<WithRealtimeInterface> & T;
};

export interface WithRealtimeInterface {
  realtime?: RealtimeConnection;
  connectRealtime(): Promise<void>;
  disconnectRealtime(): Promise<void>;
  realtimeJoinShop(shopName: string): Promise<void>;
  realtimeLeaveShop(): Promise<void>;
}
